import { inject } from '@angular/core';

import type {
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  ColumnId,
  PropsOf,
  WithSlots,
} from '@blaze-runtime/engine-domain';
import {
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSubsection,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetColumn, getColumnKey } from '@blaze-shared/schema-domain';

import { RuntimeFacade } from '../../+state';
import { isElementType, isWizard } from '../../canvas-elements/element-model/element-type-guards';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import {
  createWizardQuestionStep,
  getWizardQuestionableColumns,
  getWizardQuestionColumnKeys,
  insertWizardQuestionSteps,
} from './wizard-question-steps';
import {
  wizardConfirmationSubsections,
  wizardRequiredAndVisibilityFields,
} from './wizard-steps.props-config';
import { type WizardPropsModel, type WizardStep } from './wizard.models';
import { getWizardQuestionFieldOfForm, resolveWizardDatasetId } from './wizard.utils';

/** The tooltip on the disabled Questions knob - the whole list, not the Add button alone. */
const NO_DATA_SOURCE_MESSAGE = 'Select a data source to add questions';

export function wizardQuestionsField(
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> {
  return {
    path,
    inputType: 'collectionList',
    label,
    // keep the dialog mirrors in sync when questions are removed/reordered from the list
    valueChanged: (_model: unknown, value: unknown) =>
      questionStepsMirror((value as WizardStep[]) ?? []),
    inputOptions: {
      fieldAction: 'COLLECTION_EDIT_QUESTION',
      fieldPaneTitle: (step: WizardStep) =>
        step.type === 'confirmation' ? 'Confirmation' : 'Edit Question',
      addFieldPaneTitle: 'Add Question',
      addFieldConfig: (model: WizardPropsModel): PropertiesConfigSubsection[] =>
        wizardAddQuestionSubsections(model, path),
      fieldConfig: (index: number, _model: WizardPropsModel, steps: WizardStep[] | undefined) =>
        steps?.[index]?.type === 'confirmation'
          ? wizardConfirmationSubsections(path, index)
          : wizardQuestionSubsections(path, index),
      // the question Text element owns the text - the list label reads it live, falling back
      // to the step label (a confirmation step has no question element at all)
      labelFromElement: { idPath: 'questionElementId', valuePath: 'content.input.value' },
      listLabelPaths: ['label'],
      selectable: true,
      // there is nothing to ask about until a data source resolves, and the dialog would open
      // on an empty column list - re-evaluated on every model change by the isKnobDisabled pipe
      disableWhen: (model: WizardPropsModel) =>
        resolveWizardDatasetId(inject(SchemaService), model) ? undefined : NO_DATA_SOURCE_MESSAGE,
    },
  };
}

/**
 * The Add Question dialog renders from this mirror alone: content.questionsEnabled, the
 * column keys that already have a question step, which is the toggle-array's value. The key
 * must stay a flat props path (handleValueObservers treats top-level keys as paths); a
 * nested object would overwrite the whole `content` prop.
 */
function questionStepsMirror(steps: WizardStep[]): Record<string, unknown> {
  return { 'content.questionsEnabled': getWizardQuestionColumnKeys(steps) };
}

/** Every write of the questionnaire steps list must also rewrite its dialog mirrors. */
function questionStepsUpdate(path: string, nextSteps: WizardStep[]): Record<string, unknown> {
  return {
    [path]: nextSteps,
    ...questionStepsMirror(nextSteps),
  };
}

function wizardAddQuestionSubsections(
  model: WizardPropsModel,
  path: string,
): PropertiesConfigSubsection[] {
  const schemaService = inject(SchemaService);
  const datasetId = resolveWizardDatasetId(schemaService, model);
  // why the DATASET schema and not the bound workflow's: the generation core builds every
  // question's form from the dataset's columns, and a workflow may project or compute columns
  // the dataset has not got - offering one would name a column the core cannot find.
  const schema = datasetId ? schemaService.getDatasetSchema(datasetId) : undefined;
  if (!schema) {
    // the only reachable case: the knob's disableWhen keeps the dialog shut until the binding
    // resolves, so a missing schema here means it has not been cached yet. The dialog's config
    // is built once when it opens, so reopening is what picks the columns up.
    return [
      epc.subsections.subsection('content.questionsEnabled', '', [
        epc.fields.textInfo(
          '',
          'The data source is still loading. Close and reopen to pick its columns.',
          { header: true },
        ),
      ]),
    ];
  }
  const columns = getWizardQuestionableColumns(schema.columns);
  return [
    epc.subsections.subsection(
      'content.questionsEnabled',
      '',
      [
        epc.utils.observedField(
          epc.fields.toggleArrayField(
            'content.questionsEnabled',
            '',
            () => columns.map((column) => ({ label: column.name, value: getColumnKey(column) })),
            { searchEnabled: true, selectAllEnabled: true },
          ),
          (observedModel: WizardPropsModel, value) =>
            questionSelectionUpdate(observedModel, columns, path, (value as string[]) ?? []),
        ),
      ],
      { hideLabel: true },
    ),
  ];
}

/**
 * Applies the Add Question selection: steps of unchecked columns are removed, checked
 * columns without a step get a new question inserted before the confirmation step.
 */
function questionSelectionUpdate(
  model: WizardPropsModel,
  columns: DatasetColumn[],
  path: string,
  selectedKeys: string[],
): Record<string, unknown> {
  const steps = model.content.steps?.items || [];
  const kept = steps.filter((step) => !step.columnKey || selectedKeys.includes(step.columnKey));
  const existingKeys = new Set(steps.map(({ columnKey }) => columnKey));
  const added = selectedKeys
    .filter((key) => !existingKeys.has(key))
    .map((key) => columns.find((column) => getColumnKey(column) === key))
    // a key the dialog never offered names no column, and would mint a labelless question
    .filter((column): column is DatasetColumn => !!column)
    .map(createWizardQuestionStep);
  return questionStepsUpdate(path, insertWizardQuestionSteps(kept, added));
}

function wizardQuestionSubsections(path: string, index: number): PropertiesConfigSubsection[] {
  return [
    epc.subsections.subsection(
      `${path}.${index}`,
      '',
      [
        epc.fields.textInput(`${path}.${index}.label`, 'Step Label', {
          // every stepper style renders the step label except the bar (plain progress bars)
          hideWhen: (model) => model.content.layout.style === 'bar',
        }),
        // The generated Text elements own these texts outright - no seed on the step to fall
        // back to. The question's element always exists while its step does; the
        // explanation's exists while `explanationEnabled` is true.
        epc.fields.remoteTextField(`${path}.${index}.questionElementId`, 'Question', {
          targetId: `${path}.${index}.questionElementId`,
          targetValuePath: 'content.input.value',
          multiline: true,
          placeholder: 'Type your question here',
        }),
        epc.fields.remoteTextField(`${path}.${index}.explanationElementId`, 'Explanation', {
          targetId: `${path}.${index}.explanationElementId`,
          targetValuePath: 'content.input.value',
          // typing creates the explanation and clearing removes it, by writing the flag
          // regeneration reads - this field is the only removal affordance there is, since
          // the canvas refuses to delete a generated element
          targetExistsPath: `${path}.${index}.explanationEnabled`,
          multiline: true,
          placeholder: 'Explain the question',
        }),
        epc.fields.remoteTextField(`${path}.${index}.fieldElementId`, 'Placeholder', {
          // no seed: the field element owns the placeholder outright; the knob stays
          // disabled until the question's form exists
          targetId: (props, selectedElementId) => {
            const step = (props as PropsOf<'wizard'>).content?.steps?.items[index];
            // projects saved before the fieldElementId heal: resolve by containment
            return (
              step?.fieldElementId ??
              questionFieldOf(selectedElementId as CanvasElementId | undefined, step)?.id
            );
          },
          targetValuePath: (elementType) => fieldPlaceholderPath(elementType),
          // hidden when the question's field type has no placeholder
          hideWhen: (
            props: PropsOf<'wizard'> & {
              root?: WithSlots<ColumnId> & { elementId: CanvasElementId };
            },
          ) => {
            const step = props.content?.steps?.items[index];
            const field = questionFieldOf(props.root?.elementId, step);
            return !!field && !fieldPlaceholderPath(field.type);
          },
        }),
      ],
      { hideLabel: true },
    ),
    epc.subsections.subsection(
      `${path}.${index}`,
      'Options',
      wizardRequiredAndVisibilityFields(path, index),
      { classes: ['break-top'] },
    ),
  ];
}

/**
 * The generated field element bound to the question's column, resolved by containment
 * (wizard slot -> form -> field). Runs in an injection context (hideWhen / knob resolver).
 */
function questionFieldOf(
  wizardElementId: CanvasElementId | undefined,
  step: WizardStep | undefined,
): CanvasElement | undefined {
  if (!step?.columnKey || !step.slotId || !wizardElementId) {
    return undefined;
  }
  const elements = inject(RuntimeFacade).elementsValue;
  const wizard = elements[wizardElementId];
  const form = isWizard(wizard)
    ? wizard.slots[step.slotId]?.children
        .map((childId) => elements[childId])
        .find((child) => isElementType(child, 'formContainer'))
    : undefined;
  return isElementType(form, 'formContainer')
    ? getWizardQuestionFieldOfForm(elements, form, step.columnKey)
    : undefined;
}

/** Where the element type keeps its placeholder, declared on its definition. */
function fieldPlaceholderPath(elementType: string): string | undefined {
  return inject(ElementDefinitionsService).getDefinition(elementType as CanvasElementType)
    ?.metaUiFeatures?.paths?.placeholder;
}
